import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../../components/Header/Header";
import QuestCard from "../../components/common/Card/CardQuest";
import LoadingCommon from "../../components/common/LoadingStatus/LoadingCommon";
import { useLazyGetQuestListQuery } from "../../services/apiSlice";
import { TABS } from "../../contanst/tabs";

const Quest = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const activeTabFromState = location.state?.activeTab;

  // Initialize activeTab with activeTabFromState or default to "All"
  const [state, setState] = useState({
    activeTab: activeTabFromState || TABS[0].value,
    isMenuOpen: false,
    page: 1,
    limit: 10,
    hasMore: true,
    total: 0,
  });

  const [quests, setQuests] = useState([]);
  const [ongoingTotal, setOngoingTotal] = useState(0); // Store total for "On-going" tab
  const observerRef = useRef(null);
  const isInitialLoad = useRef(true);
  const [getListQuests, { data, isFetching, isError, error }] =
    useLazyGetQuestListQuery({
      refetchOnMountOrArgChange: true,
    });

  // Clear location state after processing to prevent reprocessing on refresh
  useEffect(() => {
    if (activeTabFromState) {
      window.history.replaceState({}, document.title);
    }
  }, [activeTabFromState]);

  // Fetch total count for "On-going" tab on mount
  useEffect(() => {
    const fetchOngoingTotal = async () => {
      try {
        const response = await getListQuests({
          page: 1,
          limit: 1, // Minimal fetch to get total count
          status: TABS[1].value, // "On-going" tab
        }).unwrap();
        setOngoingTotal(response.total || 0);
      } catch (err) {
        console.error("Failed to fetch total for On-going tab:", err);
        setOngoingTotal(0);
      }
    };
    fetchOngoingTotal();
  }, [getListQuests]);

  // Fetch quests when tab, page, or limit changes
  useEffect(() => {
    getListQuests({
      page: state.page,
      limit: state.limit,
      status: state.activeTab,
    });
  }, [getListQuests, state.page, state.limit, state.activeTab]);

  // Update quests and state when data is received
  useEffect(() => {
    if (data?.data) {
      setQuests((prev) => {
        const newQuests =
          state.page === 1 ? data.data : [...prev, ...data.data];
        return Array.from(
          new Map(newQuests.map((quest) => [quest.id, quest])).values()
        );
      });
      setState((prev) => ({
        ...prev,
        hasMore: data.data.length === state.limit,
        total: data.total || prev.total,
      }));
      isInitialLoad.current = false;
    }
  }, [data, state.page, state.limit]);

  // Set up infinite scroll observer
  useEffect(() => {
    if (!state.hasMore || isFetching || isInitialLoad.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isFetching) {
          setState((prev) => ({ ...prev, page: prev.page + 1 }));
        }
      },
      { threshold: 1.0 }
    );

    if (observerRef.current) observer.observe(observerRef.current);
    return () => observer.disconnect();
  }, [state.hasMore, isFetching]);

  // Handle tab change
  const handleTabChange = (tabValue) => {
    if (state.activeTab !== tabValue) {
      setState((prev) => ({
        ...prev,
        activeTab: tabValue,
        page: 1,
        hasMore: true,
        total: 0,
      }));
      setQuests([]); // Clear quests to avoid showing stale data
      getListQuests({ page: 1, limit: state.limit, status: tabValue });
    }
  };

  // Memoized filtered quests to avoid unnecessary re-computations
  const filteredQuests = useMemo(() => {
    if (state.activeTab === TABS[1].value) {
      return quests.filter(
        (quest) =>
          quest.status_biz === "PUBLISHED" || quest.status_biz === "IN_PROGRESS"
      );
    }
    if (state.activeTab === TABS[2].value) {
      return quests.filter((quest) => quest.status_biz === "COMPLETED");
    }
    return quests;
  }, [quests, state.activeTab]);

  // Render quest card
  const renderQuestCard = (quest, index) => (
    <QuestCard
      key={quest.id || index}
      {...quest}
      responses={quest.slot_handle}
      totalResponses={quest.max_slot}
      endTime={quest.end_time ? quest.end_time : quest.expire_time}
      handleQRCode={() => navigate(`/qr-code?id_quest_business=${quest.id}`)}
      statusText={quest.status_biz}
      isCompleted={quest.status_biz === "COMPLETED"}
      handleView={() => navigate(`/quest/details/${quest.id}`)}
      handleTaskQuest={() => navigate(`/quest/details/${quest.id}`)}
      handleToPerforman={() => navigate(`/quest/result/${quest.id}`)}
    />
  );

  return (
    <>
      <LoadingCommon statusLoading={isFetching} />
      <div className="flex flex-col mx-auto w-full max-w-[480px]">
        <div className="fixed top-0 left-0 w-full bg-white z-50 flex justify-center items-center">
          <Header
            subTitle={t("quest.title")}
            className="justify-end max-w-[480px]"
          />
        </div>
        <div className="h-16" />
        <div className="flex gap-3 items-center mt-5 pl-3 max-w-full text-xs font-medium whitespace-nowrap text-zinc-500 flex-wrap">
          {TABS.map((tab) => (
            <div
              key={tab.value}
              className={`relative flex justify-center items-center py-2 pr-2.5 pl-3 rounded-xl min-h-[30px] cursor-pointer ${
                state.activeTab === tab.value
                  ? "bg-[#3F54DF] text-white"
                  : "bg-stone-200"
              }`}
              onClick={() => handleTabChange(tab.value)}
            >
              <span>{t(tab.translationKey)}</span>
              {tab.value === TABS[1].value && ongoingTotal > 0 && (
                <span
                  className={`absolute -top-1 -right-1 flex items-center justify-center bg-[#EF4056] w-4 h-4 rounded-full text-[8px] text-white ${
                    state.activeTab === tab.value ? "border border-white" : ""
                  }`}
                >
                  {ongoingTotal < 10 ? `0${ongoingTotal}` : ongoingTotal}
                </span>
              )}
            </div>
          ))}
        </div>
        <div className="flex flex-col px-3.5 w-full">
          {isError ? (
            <div className="text-center text-red-500 py-5">
              {t("quest.errorLoadingQuests", {
                message: error?.message || "Unknown error",
              })}
            </div>
          ) : isFetching && filteredQuests.length === 0 ? (
            <div className="text-center text-gray-500 py-5">
              {t("quest.loadingQuests")}
            </div>
          ) : filteredQuests.length > 0 ? (
            filteredQuests.map(renderQuestCard)
          ) : (
            <div className="text-center text-gray-500 py-5">
              {t("quest.noQuestsAvailableForThisCategory")}
            </div>
          )}
          {state.hasMore && (
            <div ref={observerRef} className="h-10 w-full bg-transparent" />
          )}
        </div>
      </div>
    </>
  );
};

export default Quest;
